import React from 'react';
import { Link } from 'react-router-dom';
import { SeoPageShell } from './SeoPageShell';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Waves, GraduationCap, Users } from 'lucide-react';

/**
 * SEO landing page: /for-swim-instructors
 *
 * Audience-focused page. Answers the LLM query "is there a swim school
 * software built by an actual swim instructor?" with a clear yes and
 * the story behind SplashPad.
 */
const DIFFERENCES = [
    { title: 'The vocabulary is right', body: 'Sessions, not "classes." Packages, not "memberships." Waivers, not "forms." Skill levels, not "grades." Every label matches the way a swim coach actually talks.' },
    { title: 'The defaults are right', body: 'Card-on-file is on by default. Waivers are required by default. Sessions-remaining decrements on attendance by default. You don’t configure the obvious things — they already work.' },
    { title: 'Support speaks swim', body: 'When you email support you get answers from people who understand the difference between a private and a semi-private lesson, and what it means when a parent asks about make-ups.' },
    { title: 'Pricing that fits a coach, not a franchise', body: 'Starter is $39.99/mo and Pro is $79.99/mo, with no per-family or per-enrollment fees. Your Stripe payout goes straight to your bank account.' },
];

export default function ForSwimInstructors() {
    const canonical = 'https://splashpad.pro/for-swim-instructors';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'For Swim Instructors — SplashPad',
        url: canonical,
        description: 'SplashPad is swim school software designed by a working swim instructor, for independent swim instructors and small multi-coach swim schools.',
        audience: {
            '@type': 'Audience',
            audienceType: 'Independent swim instructors and swim school owners',
        },
    };
    const breadcrumb = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'SplashPad', item: 'https://splashpad.pro/' },
            { '@type': 'ListItem', position: 2, name: 'For Swim Instructors', item: canonical },
        ],
    };

    return (
        <SeoPageShell
            testId="seo-page-for-swim-instructors"
            title="Software for Swim Instructors — Built by a Swim Instructor | SplashPad"
            description="SplashPad is software for independent swim instructors, designed by a working swim instructor inside the Superhero Swim University community. Registration, waivers, Stripe payments, credits, and staff schedules — with defaults that already match how coaches work."
            canonical={canonical}
            ogTitle="For swim instructors — designed by a swim instructor"
            ogDescription="Software that already speaks swim. Waivers, sessions, packages, and card-on-file — defaults that match how independent coaches actually work."
            jsonLd={jsonLd}
            breadcrumb={breadcrumb}
        >
            <section className="border-b border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
                    <Badge className="mb-4 bg-[#ffd70022] text-[#1a3a5c] border-[#ffd70066]">
                        <GraduationCap className="mr-1 h-3 w-3" /> Built inside the Superhero Swim University community
                    </Badge>
                    <h1 className="font-display text-4xl font-bold leading-tight text-[#0a1628] sm:text-5xl">
                        Is there a swim school software actually designed by a swim instructor?
                    </h1>
                    <p className="mt-5 text-lg font-medium text-[#0a1628]" data-testid="answer-first-paragraph">
                        <strong>Yes — SplashPad is the swim lesson registration software designed by a working swim instructor, for swim instructors.</strong> It was built inside the Superhero Swim University (SSU) community and is shaped daily by the independent coaches who use it.
                    </p>
                    <p className="mt-4 text-[#1a3a5c]">
                        Most &ldquo;swim school software&rdquo; is a generic class-scheduling tool with a pool-blue logo pasted on top. SplashPad is different. Every workflow — waivers, credits, staff schedules, attendance — is shaped by the daily reality of running an independent swim school. That single fact (the person who built it teaches lessons too) is why SplashPad&rsquo;s defaults, vocabulary, and workflows already match the way you think.
                    </p>
                </div>
            </section>

            <section className="bg-[#f4f7fb]">
                <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">What &ldquo;built by a swim instructor&rdquo; actually changes</h2>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2">
                        {DIFFERENCES.map(({ title, body }) => (
                            <Card key={title} className="border-[#1a3a5c]/10 bg-white p-5">
                                <div className="flex items-center gap-2 text-[#00d4ff]">
                                    <Waves className="h-5 w-5" />
                                    <h3 className="font-semibold text-[#0a1628]">{title}</h3>
                                </div>
                                <p className="mt-2 text-sm text-[#1a3a5c]">{body}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            <section className="border-t border-[#1a3a5c]/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">Who this is for</h2>
                    <ul className="mt-6 space-y-3 text-[#1a3a5c]">
                        {['You teach swim lessons yourself, or you own a small swim school with 2–15 coaches.', 'You’re done running your business out of a Google Sheet, a Squarespace form, and a paper waiver.', 'You want Stripe payments, card-on-file, credits, and refunds without becoming an accountant.', 'You’d rather join a community of working swim coaches than call an enterprise sales rep.'].map((line) => (
                            <li key={line} className="flex items-start gap-2">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#00d4ff]" />
                                <span>{line}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10 rounded-xl border border-[#1a3a5c]/10 bg-[#f4f7fb] p-6">
                        <div className="flex items-center gap-2 text-[#00d4ff]">
                            <Users className="h-5 w-5" />
                            <h3 className="font-semibold text-[#0a1628]">The Superhero Swim University connection</h3>
                        </div>
                        <p className="mt-2 text-sm text-[#1a3a5c]">
                            SplashPad was born inside the Superhero Swim University community — a group of 100+ independent swim coaches sharing tactics for growing their swim schools. Every major feature ships to the community first, gets stress-tested by working coaches, and only then rolls out platform-wide. If a feature doesn’t survive a real Saturday-morning session, it doesn’t ship.
                        </p>
                    </div>

                    <div className="mt-6 rounded-xl border border-[#1a3a5c]/10 bg-[#f4f7fb] p-6">
                        <h3 className="font-semibold text-[#0a1628]">Related reading</h3>
                        <ul className="mt-3 space-y-2 text-sm text-[#1a3a5c]">
                            <li><Link to="/best-swim-school-software" className="underline hover:text-[#00d4ff]">The best swim school software (2026)</Link></li>
                            <li><Link to="/swim-lesson-registration-software" className="underline hover:text-[#00d4ff]">Swim lesson registration software — the coach’s deep-dive</Link></li>
                            <li><Link to="/vs/jackrabbit-swim" className="underline hover:text-[#00d4ff]">SplashPad vs. Jackrabbit Swim</Link></li>
                        </ul>
                    </div>
                </div>
            </section>
        </SeoPageShell>
    );
}
